import { Button, Card, Box, SubscriberStarIcon } from '@deckee/deck-hand';
import React, { FC, FormEvent } from 'react';
import styled, { css } from 'styled-components';
import IconWithTextLockup from '../../../../lockups/IconWithTextLockup/IconWithTextLockup';
import Shimmer from '../../../../shared/Shimmer/Shimmer';
import { useRelativeTimeDetailed } from '../../helpers/relative-time';
import { SignalAppearanceTypes } from '../SignalAppearanceChoices/SignalAppearance.types';
import { getAppearanceIcon } from '../SignalAppearanceChoices/SignalAppearanceButton';

export interface UserShareItemProps {
  memberId: string;
  displayName: string;
  timestamp?: string;
  description?: string | undefined;
  distanceFromMember?: string;
  subscriber?: boolean;
  userImageId?: string;
  buttonText?: string;
  padded?: boolean;
  contactBeingRemoved?: string | undefined | null;
  buttonAction?: (memberId: string) => void;
  itemAction?: (memberId: string) => void;
}

const StyledUserShareItem = styled(Card)`
  ${({ $padded, theme }) =>
    $padded &&
    css`
      padding: ${theme.space[3]}px;
    `}

  ${({ $hasAction, theme }) =>
    $hasAction &&
    css`
      cursor: pointer;
      :hover {
        background-color: ${theme.colors.lightgrey};
      }
    `};
`;

const UserShareItem: FC<UserShareItemProps> = ({
  memberId,
  displayName,
  userImageId,
  contactBeingRemoved,
  padded,
  buttonText,
  timestamp,
  distanceFromMember,
  description,
  subscriber,
  itemAction,
  buttonAction,
}) => {
  const removingContact = contactBeingRemoved === memberId;
  const { lastKnownRelativeTime: relativeTime, isNow } =
    useRelativeTimeDetailed(timestamp || '');

  const handleSelect = () => {
    if (itemAction) {
      itemAction(memberId);
    }
  };

  const handleButtonClick = (e: FormEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (buttonAction) {
      buttonAction(memberId);
    }
  };

  return (
    <StyledUserShareItem
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      onClick={handleSelect}
      $padded={padded}
      $hasAction={!!itemAction}
    >
      <Shimmer on={removingContact}>
        <IconWithTextLockup
          image={getAppearanceIcon(
            userImageId
              ? SignalAppearanceTypes.Profile
              : SignalAppearanceTypes.Anonymous,
            userImageId || '',
          )}
          heading={
            subscriber ? (
              <Box display="flex" alignItems="center">
                <Box mr="2">{displayName}</Box>
                <SubscriberStarIcon width="16px" />
              </Box>
            ) : (
              displayName
            )
          }
          description={
            relativeTime &&
            !distanceFromMember?.includes('NaN') &&
            distanceFromMember
              ? `${relativeTime} • ${distanceFromMember} away`
              : relativeTime || description || undefined
          }
          descriptionColor={isNow ? 'success' : undefined}
        />
      </Shimmer>
      {buttonAction && (
        <Shimmer on={removingContact}>
          <Button ml={1} onClick={handleButtonClick} outlined>
            {buttonText}
          </Button>
        </Shimmer>
      )}
    </StyledUserShareItem>
  );
};

UserShareItem.defaultProps = {
  buttonAction: undefined,
  itemAction: undefined,
  contactBeingRemoved: undefined,
  timestamp: undefined,
  buttonText: 'Remove',
  description: undefined,
  distanceFromMember: undefined,
  userImageId: undefined,
  padded: false,
  subscriber: false,
};

export default UserShareItem;
